import template from './uc-media-upload.html.twig';
import './uc-media-upload.scss';

const { Mixin } = Shopware;

/**
 * uc-media-upload
 *
 * Media picker for plugin config cards (used via
 * <component name="uc-media-upload"> in config.xml).
 *
 * Uses the media library MODAL (sw-media-modal-v2, which supports uploading
 * and picking) instead of sw-media-field — whose popover renders detached
 * from the field on some Shopware versions.
 *
 * Enforces a client-side max file size (default 5 MB): oversized selections
 * are rejected with an error notification. The storefront subscriber
 * additionally enforces the same limit server-side.
 *
 * Value: media UUID (string) or null.
 */
Shopware.Component.register('uc-media-upload', {
    template,

    inject: ['repositoryFactory'],

    mixins: [
        Mixin.getByName('notification'),
    ],

    props: {
        value: {
            type: String,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: '',
        },
        helpText: {
            type: String,
            required: false,
            default: '',
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        /** Maximum allowed file size in bytes (default 5 MB) */
        maxFileSize: {
            type: Number,
            required: false,
            default: 5 * 1024 * 1024,
        },
    },

    emits: ['update:value'],

    data() {
        return {
            mediaEntity: null,
            showMediaModal: false,
        };
    },

    computed: {
        mediaRepository() {
            return this.repositoryFactory.create('media');
        },

        maxFileSizeMb() {
            return Math.round(this.maxFileSize / (1024 * 1024));
        },

        previewUrl() {
            return this.mediaEntity?.url ?? null;
        },

        fileName() {
            if (!this.mediaEntity) return '';
            const ext = this.mediaEntity.fileExtension ? `.${this.mediaEntity.fileExtension}` : '';
            return `${this.mediaEntity.fileName ?? ''}${ext}`;
        },
    },

    watch: {
        value() {
            this.loadMedia();
        },
    },

    created() {
        this.loadMedia();
    },

    methods: {
        async loadMedia() {
            if (!this.value) {
                this.mediaEntity = null;
                return;
            }
            try {
                this.mediaEntity = await this.mediaRepository.get(this.value, Shopware.Context.api);
            } catch {
                this.mediaEntity = null;
            }
        },

        onMediaSelectionChange(selection) {
            const media = Array.isArray(selection) ? selection[0] : selection;
            this.showMediaModal = false;
            if (!media) {
                return;
            }

            if ((media.fileSize ?? 0) > this.maxFileSize) {
                this.createNotificationError({
                    message: this.$tc(
                        'sellops-ultimate-cart.mediaUpload.fileTooLarge',
                        0,
                        { limit: this.maxFileSizeMb }
                    ),
                });
                return;
            }

            this.mediaEntity = media;
            this.$emit('update:value', media.id);
        },

        removeMedia() {
            this.mediaEntity = null;
            this.$emit('update:value', null);
        },
    },
});
